import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { AlertCircle, ArrowLeft } from 'lucide-react';
// Diseño «Dividida» (8-oct-2026, Carlos: «el login no tiene la misma marca que el registro»): misma mitad azul,
// fuente y estilos de campo que el registro (AuthMarca). La lógica de acceso y de recuperación no cambió.
import { PanelMarca, FUENTE, INPUT, LABEL } from '../components/AuthMarca';

// Soporte: la frase «necesito ayuda con Lealify» la reconoce el bot (ORIGENES_SOPORTE) y te avisa con la ficha.
const WA_AYUDA = `https://wa.me/526861326204?text=${encodeURIComponent('Hola, necesito ayuda con Lealify: no puedo entrar a mi cuenta.')}`;
const BOTON = 'h-12 w-full rounded-full bg-[#0057B8] text-[15px] font-bold text-white shadow-lg shadow-[#0057B8]/25 transition hover:bg-[#004494] focus:outline-none focus:ring-4 focus:ring-[#0057B8]/30 active:scale-[0.99]';
import { baseAuthApi } from '../../../api/baseAuthApi';
import { useError } from '../../../context/ErrorContext';

const Login: React.FC = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const { login, loading, error: authError, clearError: clearAuthError } = useAuth();
    const { addError, clearErrors } = useError();
    
    // ✅ Estado para controlar la vista de "Recuperar Contraseña"
    const [isResetting, setIsResetting] = useState(false);
    const [resetMessage, setResetMessage] = useState<string | null>(null);
    const [resetLoading, setResetLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        clearErrors();
        if (clearAuthError) clearAuthError();
        
        if (loading) return;
        
        if (!email || !password) {
            addError("Por favor, completa todos los campos", "validation", "login-form");
            return;
        }
        try {
            await login(email, password);
            // navigate('/dashboard'); // Opcional: forzar navegación si PublicRoute no reacciona rápido
        } catch (err) {
            console.error("[Login.tsx] Error login:", err);
        }
    };

    // ✅ Manejador para enviar el correo de recuperación
    const handlePasswordReset = async (e: React.FormEvent) => {
        e.preventDefault();
        setResetMessage(null);
        clearErrors();

        if (!email) {
            addError("Ingresa tu correo electrónico para enviarte las instrucciones.", "validation", "reset-password");
            return;
        }

        setResetLoading(true);
        try {
            // Recuperación contra lealify-base. El backend responde ok siempre (no revela si la
            // cuenta existe) y manda el correo con enlace a /restablecer?token=...
            await baseAuthApi.forgotPassword(email);
            setResetMessage("Si existe una cuenta con este correo, te enviamos un enlace de recuperación. Revisa tu bandeja de entrada.");
        } catch (error: any) {
            console.error("Error sending reset email:", error);
            addError("Error al enviar el correo. Intenta de nuevo más tarde.", "auth", "reset-password");
        } finally {
            setResetLoading(false);
        }
    };

    const handleInputChange = (setter: React.Dispatch<React.SetStateAction<string>>, value: string) => {
        setter(value);
        if (authError && clearAuthError) clearAuthError();
        clearErrors();
        setResetMessage(null); // Limpiar mensajes de éxito al escribir
    };


    // flex-shrink-0: #root mide 100vh y es flex (misma trampa que el registro en celular).
    return (
        <div className="force-light min-h-screen flex-shrink-0 flex flex-col md:flex-row bg-white" style={FUENTE}>
            <PanelMarca
                titulo="Tus clientes vuelven más seguido."
                lineaMovil=""
                beneficios={['Tu kiosco y tu QR, listos en el mostrador', 'Quién vuelve y quién no, de un vistazo', 'Tus clientes, sus puntos y sus premios']}
            />

            <main className="flex flex-1 items-center justify-center px-5 py-8 md:px-12 md:py-12">
                <div className="w-full max-w-[400px]">

                    {/* Error de acceso */}
                    {authError && !isResetting && (
                        <div className="mb-5 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 animate-fade-in">
                            <AlertCircle className="h-5 w-5 flex-shrink-0 text-red-500" aria-hidden="true" />
                            <p className="text-sm text-red-700">{authError}</p>
                        </div>
                    )}

                    {isResetting ? (
                        // VISTA: Recuperar contraseña
                        <div className="animate-fade-in">
                            <h2 style={FUENTE} className="text-center text-[28px] md:text-[32px] font-extrabold tracking-tight text-[#0E1E3A]">Recupera tu contraseña</h2>
                            <p className="mt-2 mb-7 text-center text-sm text-slate-600">
                                Escribe tu correo y te enviamos un enlace para crear una nueva.
                            </p>

                            {resetMessage && (
                                <div className="mb-5 rounded-xl border border-green-200 bg-green-50 p-3 text-center text-sm text-green-700">
                                    {resetMessage}
                                </div>
                            )}

                            <form onSubmit={handlePasswordReset} className="space-y-4">
                                <div>
                                    <label htmlFor="reset-email" className={LABEL}>Correo electrónico</label>
                                    <input id="reset-email" name="reset-email" type="email" autoComplete="email" required value={email} onChange={(e) => handleInputChange(setEmail, e.target.value)} className={INPUT} placeholder="correo@ejemplo.com" />
                                </div>
                                <button type="submit" disabled={resetLoading || !!resetMessage}
                                    className={`${BOTON} ${resetLoading || !!resetMessage ? 'cursor-not-allowed opacity-70' : ''}`}>
                                    {resetLoading ? 'Enviando...' : 'Enviar enlace'}
                                </button>
                            </form>

                            <button
                                onClick={() => { setIsResetting(false); setResetMessage(null); clearErrors(); }}
                                className="mt-5 flex w-full items-center justify-center text-sm font-semibold text-slate-600 hover:text-[#0E1E3A] transition-colors"
                            >
                                <ArrowLeft className="mr-2 h-4 w-4" /> Volver a iniciar sesión
                            </button>
                        </div>
                    ) : (
                        // VISTA: Iniciar sesión
                        <>
                            <h2 style={FUENTE} className="text-center text-[28px] md:text-[34px] font-extrabold tracking-tight text-[#0E1E3A]">Inicia sesión</h2>
                            <p className="mt-2 mb-7 text-center text-sm text-slate-600">Entra a tu panel de Lealify.</p>

                            <form className="space-y-4" onSubmit={handleSubmit}>
                                <div>
                                    <label htmlFor="email" className={LABEL}>Correo electrónico</label>
                                    <input id="email" name="email" type="email" autoComplete="email" required value={email} onChange={(e) => handleInputChange(setEmail, e.target.value)} className={INPUT} placeholder="correo@ejemplo.com" />
                                </div>
                                <div>
                                    <div className="mb-1.5 flex items-center justify-between">
                                        <label htmlFor="password" className="block text-sm font-semibold text-[#0E1E3A]">Contraseña</label>
                                        <button type="button" onClick={() => { setIsResetting(true); clearErrors(); }}
                                            className="text-sm font-semibold text-[#0057B8] hover:underline">
                                            ¿Olvidaste tu contraseña?
                                        </button>
                                    </div>
                                    <input id="password" name="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => handleInputChange(setPassword, e.target.value)} className={INPUT} placeholder="••••••••" />
                                </div>
                                <div className="pt-2">
                                    <button type="submit" disabled={loading} className={`${BOTON} ${loading ? 'cursor-not-allowed opacity-70' : ''}`}>
                                        {loading ? 'Iniciando sesión...' : 'Ingresar'}
                                    </button>
                                </div>
                            </form>

                            <p className="mt-5 text-center text-sm text-slate-600">
                                ¿No tienes una cuenta?{' '}
                                <Link to="/register" className="font-bold text-[#0057B8] hover:underline">Regístrate gratis</Link>
                            </p>

                            <div className="mt-6 border-t border-slate-200 pt-5 text-center">
                                <a href={WA_AYUDA} target="_blank" rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-[#0E1E3A]">
                                    <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4 fill-[#25D366]"><path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.25-.46-2.38-1.47-.88-.79-1.47-1.76-1.65-2.06-.17-.3-.02-.46.13-.6.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.6-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.22 1.36.19 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35M12.05 21.79h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37A9.86 9.86 0 0 1 2.16 11.9C2.17 6.45 6.6 2.02 12.06 2.02c2.64 0 5.12 1.03 6.99 2.9a9.82 9.82 0 0 1 2.89 6.99c0 5.45-4.43 9.88-9.89 9.88m8.41-18.3A11.81 11.81 0 0 0 12.05 0C5.5 0 .16 5.34.16 11.89c0 2.1.55 4.14 1.59 5.95L.06 24l6.3-1.65a11.88 11.88 0 0 0 5.68 1.45h.01c6.55 0 11.89-5.34 11.89-11.9 0-3.17-1.24-6.16-3.48-8.41" /></svg>
                                    ¿No puedes entrar? Escríbenos por WhatsApp
                                </a>
                            </div>
                        </>
                    )}
                </div>
            </main>
        </div>
    );
};

export default Login;
